/**
 * 麻将牌组件
 *
 * 素材：FluffyStuff/riichi-mahjong-tiles（CC0）
 *   ⚠️ 素材是**透明背景**（只有牌面图案，没有白色底板）
 *      → 必须用 CSS 画牌的底板（白底 + 圆角 + 立体阴影）
 */

import { memo, type CSSProperties } from 'react';
import { tileUrlByIndex, tileBackUrl, TILE_CHINESE_NAMES } from '../tiles.js';

/** 牌尺寸档（CSS 像素宽）*/
export type TileScale = 'xs' | 'sm' | 'md' | 'lg';

/** 各档位对应的 CSS 变量（牌宽按视口宽度自适应，见 styles.css :root） */
const SCALE_VAR: Record<TileScale, string> = {
  xs: 'var(--tile-other)',
  sm: 'var(--tile-meld)',
  md: 'var(--tile-hand)',
  lg: 'var(--tile-hand)',
};

/** 牌方向 */
export type TileOrientation = 'normal' | 'rotate-left' | 'rotate-right' | 'rotate-180';

interface TileProps {
  index?: number;
  isRed?: boolean;
  faceDown?: boolean;
  scale?: TileScale;
  orientation?: TileOrientation;
  clickable?: boolean;
  highlighted?: boolean;
  isDrawn?: boolean;
  /** 选中（两下出牌的第一下，牌抬高）*/
  selected?: boolean;
  onClick?: () => void;
  title?: string;
  className?: string;
  opacity?: number;
}

/**
 * 单张麻将牌
 *
 * 结构：外层 div（尺寸 + 旋转占位） → 内层 div（白底牌身 + 图案）
 * 底板样式在 styles.css 的 `.tile-body` 里定义
 */
export const Tile = memo(function Tile({
  index,
  isRed = false,
  faceDown = false,
  scale = 'md',
  orientation = 'normal',
  clickable = false,
  highlighted = false,
  isDrawn = false,
  selected = false,
  onClick,
  title,
  className = '',
  opacity = 1,
}: TileProps) {
  const w = SCALE_VAR[scale]; // CSS 长度表达式（如 var(--tile-hand)）
  const h = `calc(${w} * 4 / 3)`; // 麻将牌比例 3:4

  const isSideways = orientation === 'rotate-left' || orientation === 'rotate-right';

  const src = faceDown || index === undefined
    ? tileBackUrl('2x')
    : tileUrlByIndex(index, { isRed, size: '2x' });

  const label = title ?? (index !== undefined && !faceDown ? TILE_CHINESE_NAMES[index] : '牌背');

  // 旋转角度（横置时为 ±90°）
  const rotateDeg =
    orientation === 'rotate-left' ? -90
    : orientation === 'rotate-right' ? 90
    : orientation === 'rotate-180' ? 180
    : 0;

  // 旋转后的外框尺寸（90° 时宽高互换）
  const boxW = isSideways ? h : w;
  const boxH = isSideways ? w : h;

  return (
    <div
      className={`tile ${clickable ? 'tile-clickable' : ''} ${highlighted ? 'tile-highlighted' : ''} ${isDrawn ? 'tile-drawn' : ''} ${selected ? 'tile-selected' : ''} ${className}`}
      style={
        {
          '--tile-w': w, // 供 .tile-body 计算圆角（随牌宽缩放）
          width: boxW,
          height: boxH,
          opacity,
          flex: '0 0 auto',
        } as CSSProperties
      }
      onClick={clickable ? onClick : undefined}
      title={label}
      role={clickable ? 'button' : undefined}
      aria-label={label}
    >
      {/* 内层：白底牌身，负责旋转 */}
      <div
        className={`tile-body ${faceDown || index === undefined ? 'tile-body-back' : ''}`}
        style={{
          width: w,
          height: h,
          transform: rotateDeg ? `rotate(${rotateDeg}deg)` : undefined,
        }}
      >
        <img
          src={src}
          alt={label}
          draggable={false}
          className="tile-face"
        />
      </div>
    </div>
  );
});

/** 副露（明牌组）*/
interface MeldDisplayProps {
  melds: ReadonlyArray<{
    kind: string;
    start: number;
    open: boolean;
    calledIndex?: number;
  }>;
  scale?: TileScale;
  /** 是否竖排布局（上/下家）*/
  vertical?: boolean;
  /**
   * 该家相对自家视角的整体旋转角（0/90/180/270）。
   * 与舍牌河一致：自家 0°、对家 180°、上家 90°、下家 270°。
   * 副露按此角排布，使四家在各自视角下朝向自己、读序一致。
   */
  rotate?: 0 | 90 | 180 | 270;
}

const ORIENT_BY_DEG: Record<number, TileOrientation> = {
  0: 'normal',
  90: 'rotate-right',
  180: 'rotate-180',
  270: 'rotate-left',
};

export function MeldDisplay({
  melds,
  scale = 'sm',
  vertical = false,
  rotate = 0,
}: MeldDisplayProps) {
  if (melds.length === 0) return null;

  // 持有者视角下：普通牌正立(0°)，被鸣的那张横置(-90°)。
  // 合并「该家整体旋转角」后换算成屏幕朝向。
  // 180° 用整组 transform（同时翻转朝向与读序，横排外框不变，不会破坏布局）；
  // 90°/270° 竖排则逐张旋转（竖排外框不变）。
  const groupRotate = rotate === 180;
  const tileRotate = groupRotate ? 0 : rotate;
  const orientFor = (deg: number): TileOrientation =>
    ORIENT_BY_DEG[(((deg % 360) + 360) % 360)] ?? 'normal';

  return (
    <div
      className={`meld-row ${vertical ? 'meld-row-v' : ''}`}
      style={groupRotate ? { transform: 'rotate(180deg)' } : undefined}
    >
      {melds.map((m, mi) => {
        const tiles: Array<{ index: number; rotated: boolean }> = [];

        if (m.kind === 'shuntsu') {
          for (let k = 0; k < 3; k++) {
            tiles.push({ index: m.start + k, rotated: m.calledIndex === k });
          }
        } else if (m.kind === 'koutsu') {
          for (let k = 0; k < 3; k++) {
            tiles.push({ index: m.start, rotated: k === (m.calledIndex ?? 0) });
          }
        } else {
          for (let k = 0; k < 4; k++) {
            tiles.push({ index: m.start, rotated: k === (m.calledIndex ?? 0) });
          }
        }

        return (
          <div key={mi} className="meld-group">
            {tiles.map((t, ti) => (
              <Tile
                key={ti}
                index={t.index}
                scale={scale}
                orientation={orientFor(tileRotate + (t.rotated ? -90 : 0))}
              />
            ))}
          </div>
        );
      })}
    </div>
  );
}
